/* eslint-disable react/no-unused-prop-types */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
import { memo, ReactNode, useCallback, useMemo } from 'react';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';
import Switch from '@mui/material/Switch';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DeleteSVGIcon } from 'components/shared/Icons';
import { TypeComponentTemplate } from 'api/insight';

import SelectOptions from './SelectOptions';
import Question from './Question';
import LinearScale from './LinearScale';
import ShortAnswer from './ShortAnswer';
import Paragraph from './Paragraph';
import MultipleOptions from './MultipleOptions';

type TOptions = (
  id: string,
  key: 'label' | 'options',
  value?: number | string | { label: string; id: string }[]
) => void;

export interface ISurveyQuestion {
  orderItem: string;
  typeComponent: TypeComponentTemplate;
  options?: string | { label: string; id: string }[];
  isRequired?: boolean;
}
interface IProps {
  id: string;
  value: ISurveyQuestion;
  isEditable?: boolean;
  isRequired?: boolean;
  itemFull?: { idSurveyQuestion: string; isRequired: boolean };
  optionsLine?: number;
  isResponeForm?: boolean;
  isReadOnly?: boolean;
  form: UseFormReturn;
  hasDuplicate?: boolean;
  onChange: (option: string) => void;
  onRemove: (id: string) => void;
  onDuplicate?: (id: string) => void;
  onRequired: (id: string, checked: boolean) => void;
  onChangeInputs: TOptions;
}

type TOptionFields = Pick<
  IProps,
  | 'form'
  | 'id'
  | 'value'
  | 'isReadOnly'
  | 'isEditable'
  | 'onChangeInputs'
  | 'optionsLine'
  | 'isRequired'
  | 'itemFull'
>;

const optionsFields: {
  [key: string]: (props: TOptionFields) => ReactNode;
} = {
  linearScale: ({ form, id, isEditable, isReadOnly, optionsLine, itemFull, onChangeInputs }) => (
    <LinearScale
      form={form}
      id={id}
      isEditable={isEditable}
      isReadOnly={isReadOnly}
      itemFull={itemFull}
      onChangeInputs={onChangeInputs}
      optionsLine={optionsLine}
    />
  ),
  shortAnswer: ({ form, id, isReadOnly, itemFull }) => 
  <ShortAnswer form={form} id={id} isReadOnly={isReadOnly} itemFull={itemFull} />,
  paragraph: ({ form, id, isReadOnly, itemFull }) => 
  <Paragraph form={form} id={id} isReadOnly={isReadOnly} itemFull={itemFull} />,
  severalOptions: ({ form, id, isEditable, isReadOnly, itemFull, onChangeInputs, value }) => (
    <MultipleOptions
      form={form}
      id={id}
      isEditable={isEditable}
      isReadOnly={isReadOnly}
      itemFull={itemFull}
      onChangeInputs={onChangeInputs}
      optionsCreated={value?.options as { label: string; id: string }[]}
    />
  )
};

function ContentBox({
  id,
  isEditable = true,
  isReadOnly,
  hasDuplicate = false,
  optionsLine,
  isRequired,
  itemFull,
  value,
  form,
  onChange,
  onRemove,
  onRequired,
  onDuplicate,
  onChangeInputs
}: IProps) {
  const { t } = useTranslation('global');
  const handleOptionsFields = useCallback(
    (option: string) =>
      optionsFields[option]?.({
        form,
        id,
        isEditable,
        isReadOnly,
        optionsLine,
        isRequired,
        itemFull,
        value,
        onChangeInputs
      }),
    [
      form,
      id,
      isEditable,
      isReadOnly,
      optionsLine,
      isRequired,
      itemFull,
      onChangeInputs,
      value
    ]
  );

  const OptionMemo = useMemo(
    () => handleOptionsFields(value?.typeComponent),
    [handleOptionsFields, value?.typeComponent]
  );

  return (
    <Grid gap={3} container>
      {isEditable && (
        <Grid display="flex" justifyContent="space-between" width="100%">
          <Grid xs={5}>
            <SelectOptions onChange={onChange} value={value?.typeComponent} />
          </Grid>

          <Grid
            alignItems="center"
            display="flex"
            flexWrap="wrap"
            justifyContent="flex-end"
            xs={6}
          >
            <span>
              {t('survey.template.required')}
              <Switch
                checked={value?.isRequired}
                onChange={(_, checked: boolean) => onRequired(id, checked)}
                defaultChecked
              />
            </span>
            {hasDuplicate && (
              <Tooltip placement="top" title={t('survey.template.duplicate')}>
                <IconButton onClick={() => onDuplicate(id)}>
                  <ContentCopyIcon />
                </IconButton>
              </Tooltip>
            )}
            <Tooltip placement="top" title={t('common.commonActions.delete')}>
              <IconButton onClick={() => onRemove(id)}>
                <DeleteSVGIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      )}
      <Question
        form={form}
        id={id}
        isEditable={isEditable}
        isRequired={value?.isRequired}
        onChange={onChangeInputs}
      />
      {OptionMemo}
    </Grid>
  );
}

export default memo(ContentBox);
